import React from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseYMD = (value) => {
  if (!value) return undefined;
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

/**
 * Date field using the app's global Popover + Calendar (same as attendance
 * and other modules). Value in/out is a "YYYY-MM-DD" string, so it is a
 * drop-in replacement for <input type="date">.
 */
export const OTRDateField = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date',
  disabled = false,
  hasError = false,
  className,
}) => {
  const selected = parseYMD(value);
  const minDate = parseYMD(min);
  const maxDate = parseYMD(max);
  const disabledDays = [
    minDate && { before: minDate },
    maxDate && { after: maxDate },
  ].filter(Boolean);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-11 w-full justify-start text-left font-normal rounded-xl border bg-white px-3',
            hasError ? 'border-[var(--color-error)]' : 'border-[var(--color-text-border)]',
            !selected && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
          {selected ? format(selected, 'PPP') : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => date && onChange?.(toYMD(date))}
          defaultMonth={selected || minDate}
          disabled={disabledDays.length ? disabledDays : undefined}
        />
      </PopoverContent>
    </Popover>
  );
};
